import React from "react";
import { Add, CommentOutlined } from "@mui/icons-material";
import { Badge, Box, Button, Divider, Typography } from "@mui/material";
import ShipmentActionForm from "components/ShipmentAction/ShipmentActionForm";
import ShipmentActionList from "components/ShipmentAction/ShipmentActionList";
import FormDrawer from "components/Shared/FormDrawer";
import { useAuth, useTablePagination } from "hooks";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateShipmentActionMutation,
  useLazyGetShipmentActionListQuery,
  useUpdateShipmentActionMutation,
} from "store/services/shipmentActionApi";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";

const ShipmentActionTab = ({ setActionRecords }) => {
  const { isCustomer, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isContractor || isDriver;
  const location = useLocation();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const { ShipmentDetail } = useShipmentDetailContext();
  const [showActionForm, setShowActionForm] = React.useState(false);
  const [selectedAction, setSelectedAction] = React.useState(null);

  const [fetchRecords, { data: shipmentActions, isLoading: loadingShipmentAction }] =
    useLazyGetShipmentActionListQuery();
  const [addShipmentAction, { isLoading: loadingAction }] = useCreateShipmentActionMutation();
  const [updateShipmentActionById, { isLoading: updatingShipmentAction }] =
    useUpdateShipmentActionMutation();

  const totalActions = shipmentActions?.totalItems ?? 0;

  React.useEffect(() => {
    if (ShipmentDetail?._id) {
      fetchRecords({ shipmentId: ShipmentDetail?._id, page: currentPage, perPage });
      if (setActionRecords) setActionRecords(totalActions);
    }
  }, [ShipmentDetail?._id, fetchRecords, setActionRecords, totalActions, currentPage, perPage]);

  // Extract actionId from query parameters
  const queryParams = new URLSearchParams(location.search);
  const actionId = queryParams.get("actionId");

  const handleCancelShipmentAction = () => {
    setShowActionForm(false);
    setSelectedAction(null);
  };

  const handleAddAction = async (data) => {
    const payload = {
      account: data.account,
      entity: data.entity,
      shipmentId: data.shipmentId,
      actionType: data.actionType,
      whoCanClose: data.whoCanClose,
      status: data.status,
      description: data.description,
      responsible: data.responsible,
      vendor: data.vendor,
      response: data.response || "",
      systemType: data.systemType || "",

    };
    try {
      await addShipmentAction(payload).unwrap();
      toast.success("Shipment action created successfully");
      fetchRecords({ shipmentId: ShipmentDetail?._id, page: currentPage, perPage });
      handleCancelShipmentAction();
    } catch (error) {
      toast.error(error?.data?.message ?? "Couldn't create shipment action!");
    }
  };

  const handleEditShipmentAction = (action) => {
    setShowActionForm(true);
    setSelectedAction(action);
  };

  const handleUpdateShipmentAction = async (data) => {
    const payload = {
      account: data.account,
      entity: data.entity,
      shipmentId: data.shipmentId,
      actionType: data.actionType,
      whoCanClose: data.whoCanClose,
      status: data.status,
      description: data.description,
      responsible: data.responsible,
      vendor: data.vendor,
      response: data.response || "",
      id: data.id,
      systemType: data.systemType || "",
    };
    try {
      await updateShipmentActionById(payload).unwrap();
      toast.success("Shipment action successfully updated!");
      fetchRecords({ shipmentId: ShipmentDetail?._id, page: currentPage, perPage }); // Refetch list
      handleCancelShipmentAction();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update shipment action!");
    }
  };

  return (
    <>
      <Box display="flex" alignItems="end" justifyContent="space-between" padding={3}>
        <Box display="inline-flex" alignItems="center" gap={1.5}>
          <Badge color="primary">
            <CommentOutlined style={{ color: "gray" }} />
          </Badge>
          <Typography style={{ fontWeight: 500 }}>Actions</Typography>
        </Box>
        {!showActionForm && !canEdit && (
          <Button disabled={ShipmentDetail?.isActive === false}
            startIcon={<Add />} variant="outlined" onClick={() => setShowActionForm(true)}>
            Shipment Action
          </Button>
        )}
      </Box>
      <Divider />
      <ShipmentActionList
        shipmentActions={shipmentActions?.data}
        isBusy={loadingShipmentAction}
        totalAction={shipmentActions?.totalItems}
        perPage={perPage}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handlePerRowsChange={handlePerRowsChange}
        onEditAction={handleEditShipmentAction}
        selectedActionId={actionId}
        refetchActions={() => fetchRecords({ shipmentId: ShipmentDetail?._id, page: currentPage, perPage })} // Pass refetch function
      />
      <FormDrawer
        title="Shipment Action"
        open={showActionForm}
        onClose={handleCancelShipmentAction}
      >
        <ShipmentActionForm
          isBusy={updatingShipmentAction || loadingAction}
          editActionData={selectedAction}
          onSubmit={selectedAction ? handleUpdateShipmentAction : handleAddAction}
          onCancel={handleCancelShipmentAction}
        />
      </FormDrawer>
    </>
  );
};

export default ShipmentActionTab;